import {
  AccessTime,
  Add,
  CalendarToday,
  Event,
  Refresh,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import React, { useEffect, useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
  adminButtonOutlinedSx,
  adminButtonPrimarySx,
} from "../../../../components/AdminDashboard/modalStyle";
import { createEvent, getAllEvents } from "../../../../services/event.service";
import { ModalComponent } from "../../../../ui-components/Modal";
import { parseDateLocale } from "../../../../utils/parseDate";
import DeleteButton from "./OperationButtons/DeleteButton";
import UpdateButton from "./OperationButtons/UpdateButton";
import ViewButton from "./OperationButtons/ViewButton";

interface EventType {
  id: number;
  name: string;
  description: string;
  starts_at: string;
  ends_at: string;
  limitdate: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  cover: string;
  event_data?: any;
  event_address?: string;
}

export default function EventTable() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [events, setEvents] = useState([]);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const [eventStatusTab, setEventStatusTab] = useState<"active" | "finished">(
    "active",
  );
  const [open, setOpen] = React.useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | null>(
    null,
  );
  const getDateOnly = (value: any) => {
    if (!value) return "";
    if (typeof value === "string") return value.trim().slice(0, 10);
    try {
      return new Date(value).toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };
  const getLocalTodayISO = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const nextActiveFutureEvent = React.useMemo(() => {
    if (!events || !Array.isArray(events) || events.length === 0) return null;
    const todayISO = getLocalTodayISO();
    const candidates = (events as any[])
      .filter((e) => Boolean(e?.active))
      .map((e) => ({ e, startsISO: getDateOnly(e?.starts_at) }))
      .filter((x) => x.startsISO && x.startsISO >= todayISO)
      .sort((a, b) =>
        a.startsISO < b.startsISO ? -1 : a.startsISO > b.startsISO ? 1 : 0,
      );
    return candidates[0]?.e ?? null;
  }, [events]);
  const latestEvent = React.useMemo(() => {
    if (!events || events.length === 0) return null;
    const arr = [...events];
    arr.sort(
      (a: any, b: any) =>
        new Date(b.starts_at).getTime() - new Date(a.starts_at).getTime(),
    );
    return arr[0];
  }, [events]);
  const parseData = (d: any) => {
    try {
      if (!d) return {};
      if (typeof d === "string") return JSON.parse(d);
      return d;
    } catch {
      return {};
    }
  };
  const latestData = React.useMemo(
    () => parseData((latestEvent as any)?.event_data),
    [latestEvent],
  );
  const toDateInput = (value: string | undefined | null) => {
    if (!value) return "";
    return value.toString().slice(0, 10);
  };
  const toLimitDateTimeInput = (value: string | undefined | null) => {
    if (!value) return "";
    const raw = value.toString().trim();
    const normalized = raw.includes("T") ? raw : raw.replace(" ", "T");
    if (normalized.length >= 16) return normalized.slice(0, 16);
    if (normalized.length === 10) return `${normalized}T00:00`;
    return normalized;
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  function syncEvents() {
    getAllEvents()
      .then((eventsArr) => {
        setEvents(eventsArr);
        setFilteredEvents(eventsArr);
      })
      .catch((error) => {
        console.error("Erro ao buscar eventos:", error);
      });
  }

  const handleDateChange = (date: Date) => {
    setSelectedCalendarDate(date);
    const dateString = date.toISOString().split("T")[0];

    if (!events || !Array.isArray(events)) {
      setFilteredEvents([]);
      return;
    }

    const filtered = events.filter((event: EventType) => {
      const eventStartDate = new Date(event.starts_at)
        .toISOString()
        .split("T")[0];
      const eventEndDate = new Date(event.ends_at).toISOString().split("T")[0];
      return dateString >= eventStartDate && dateString <= eventEndDate;
    });

    setFilteredEvents(filtered);
  };

  const clearDateFilter = () => {
    setSelectedCalendarDate(null);
    setFilteredEvents(events || []);
  };

  const getEventsForDate = (date: Date) => {
    if (!events || !Array.isArray(events)) {
      return [];
    }

    const dateString = date.toISOString().split("T")[0];
    return events.filter((event: EventType) => {
      const eventStartDate = new Date(event.starts_at)
        .toISOString()
        .split("T")[0];
      const eventEndDate = new Date(event.ends_at).toISOString().split("T")[0];
      return dateString >= eventStartDate && dateString <= eventEndDate;
    });
  };

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view === "month") {
      const eventsForDate = getEventsForDate(date);
      if (eventsForDate.length > 0) {
        return (
          <div
            style={{
              backgroundColor: "#1976d2",
              borderRadius: "50%",
              width: "6px",
              height: "6px",
              margin: "0 auto",
              marginTop: "2px",
            }}
          />
        );
      }
    }
    return null;
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const formData = new FormData(e.target);

    const eventData = {
      name: formData.get("name") as string,
      description: formData.get("description") as string,
      starts_at: formData.get("starts_at") as string,
      ends_at: formData.get("ends_at") as string,
      limitdate: formData.get("limitdate") as string,
      active: formData.get("active") === "true",
      cover: formData.get("cover") as string,
      // API exige event_data como string
      event_data: JSON.stringify({
        autoral: {
          subscription_price_1: formData.get("autoral_sub1") || 0,
          subscription_price_2: formData.get("autoral_sub2") || 0,
          chair_price_1: formData.get("autoral_chair1") || 0,
          chair_price_2: formData.get("autoral_chair2") || 0,
          table_price_1: formData.get("autoral_table1") || 0,
          table_price_2: formData.get("autoral_table2") || 0,
          percentage: formData.get("autoral_percentage") || 0,
        },
        alimentacao: {
          subscription_price_1: formData.get("alimentacao_sub1") || 0,
          subscription_price_2: formData.get("alimentacao_sub2") || 0,
          chair_price_1: formData.get("alimentacao_chair1") || 0,
          chair_price_2: formData.get("alimentacao_chair2") || 0,
          table_price_1: formData.get("alimentacao_table1") || 0,
          table_price_2: formData.get("alimentacao_table2") || 0,
          percentage: formData.get("alimentacao_percentage") || 0,
          bike: formData.get("alimentacao_bike") || 0,
          trailer: formData.get("alimentacao_trailer") || 0,
        },
      }),
    };

    createEvent(eventData)
      .then(() => {
        syncEvents();
        handleClose();
      })
      .catch((error) => {
        console.error("Erro ao criar evento:", error);
      });
  };

  useEffect(() => {
    syncEvents();
  }, []);

  return (
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        backgroundColor: "#f8fafc",
        minHeight: "100vh",
      }}
    >
      <Grid container spacing={3}>
        {/* Calendar Section */}
        <Grid item xs={12} md={5}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow:
                "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
              border: "1px solid #e2e8f0",
              overflow: "visible",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <CalendarToday sx={{ color: "#24324D", mr: 2 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#24324D",
                    flex: 1,
                  }}
                >
                  Calendário de Eventos
                </Typography>
                {selectedCalendarDate && (
                  <Button
                    onClick={clearDateFilter}
                    size={isMobile ? "small" : "medium"}
                    sx={{
                      color: "#64748b",
                      fontSize: { xs: "0.75rem", md: "0.875rem" },
                      textTransform: "none",
                    }}
                  >
                    Limpar Filtro
                  </Button>
                )}
              </Box>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  "& .react-calendar": {
                    border: "none",
                    borderRadius: "12px",
                    backgroundColor: "#ffffff",
                    fontFamily: "inherit",
                    width: { xs: "100%", md: "auto" },
                    "& .react-calendar__tile": {
                      borderRadius: "8px",
                      border: "none",
                      "&:hover": {
                        backgroundColor: "#f1f5f9",
                      },
                    },
                    "& .react-calendar__tile--active": {
                      backgroundColor: "#24324D !important",
                      color: "white",
                    },
                  },
                }}
              >
                <Calendar
                  showNavigation={true}
                  value={selectedCalendarDate || new Date()}
                  onChange={handleDateChange}
                  tileContent={tileContent}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Next Event Section */}
        <Grid item xs={12} md={7}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow:
                "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
              border: "1px solid #e2e8f0",
              background: "linear-gradient(135deg, #24324D 0%, #1a2538 100%)",
              color: "white",
              overflow: "visible",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                <Event sx={{ color: "white", mr: 2 }} />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "white",
                  }}
                >
                  Próximo Evento
                </Typography>
              </Box>

              {nextActiveFutureEvent ? (
                <>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      mb: 2,
                      color: "white",
                      fontSize: { xs: "1.25rem", md: "2rem" },
                    }}
                  >
                    {(nextActiveFutureEvent as any).name}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      mb: 3,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccessTime
                        sx={{
                          fontSize: 18,
                          mr: 1,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      />
                      <Typography sx={{ color: "rgba(255,255,255,0.9)" }}>
                        Início:{" "}
                        {parseDateLocale(
                          getDateOnly((nextActiveFutureEvent as any).starts_at),
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <AccessTime
                        sx={{
                          fontSize: 18,
                          mr: 1,
                          color: "rgba(255,255,255,0.8)",
                        }}
                      />
                      <Typography sx={{ color: "rgba(255,255,255,0.9)" }}>
                        Fim:{" "}
                        {parseDateLocale(
                          getDateOnly((nextActiveFutureEvent as any).ends_at),
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpen}
                    sx={{
                      backgroundColor: "white",
                      color: "#24324D",
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                    fullWidth={isMobile}
                  >
                    Novo Evento
                  </Button>
                </>
              ) : (
                <>
                  <Typography sx={{ color: "rgba(255,255,255,0.8)", mb: 2 }}>
                    Nenhum evento ativo futuro no momento
                  </Typography>
                  <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={handleOpen}
                    sx={{
                      backgroundColor: "white",
                      color: "#24324D",
                      fontWeight: 600,
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                      },
                      transition: "all 0.2s ease-in-out",
                    }}
                    fullWidth={isMobile}
                  >
                    Novo Evento
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Events List Section */}
      <Grid container spacing={3} sx={{ mt: 2 }}>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow:
                "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
              border: "1px solid #e2e8f0",
              overflow: "visible",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                  flexWrap: "wrap",
                  rowGap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Event sx={{ color: "#24324D", mr: 2 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: "#24324D",
                    }}
                  >
                    Lista de Eventos
                  </Typography>
                </Box>
                <IconButton
                  onClick={syncEvents}
                  sx={{
                    color: "#64748b",
                    "&:hover": {
                      backgroundColor: "#f1f5f9",
                    },
                  }}
                >
                  <Refresh />
                </IconButton>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Tabs
                  value={eventStatusTab}
                  onChange={(_, val) => setEventStatusTab(val)}
                  sx={{
                    minHeight: 36,
                    "& .MuiTab-root": {
                      minHeight: 36,
                      textTransform: "none",
                      fontWeight: 600,
                    },
                  }}
                >
                  <Tab
                    value="active"
                    label={`Eventos ativos (${
                      Array.isArray(filteredEvents)
                        ? (filteredEvents as any[]).filter((e) => e?.active)
                            .length
                        : 0
                    })`}
                  />
                  <Tab
                    value="finished"
                    label={`Eventos finalizados (${
                      Array.isArray(filteredEvents)
                        ? (filteredEvents as any[]).filter((e) => !e?.active)
                            .length
                        : 0
                    })`}
                  />
                </Tabs>
              </Box>

              <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
                <Table size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                        Nome
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "#475569" }}>
                        Data de Início
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#475569",
                          display: { xs: "none", md: "table-cell" },
                        }}
                      >
                        Data de Fim
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#475569",
                          minWidth: { md: 220 },
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#475569",
                          minWidth: { md: 220 },
                        }}
                      >
                        Ações
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Array.isArray(filteredEvents)
                      ? (filteredEvents as any[]).filter((e) =>
                          eventStatusTab === "active"
                            ? Boolean(e?.active)
                            : !e?.active,
                        )
                      : []
                    ).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Box sx={{ py: 4, textAlign: "center" }}>
                            Nenhum evento encontrado.
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      (Array.isArray(filteredEvents)
                        ? (filteredEvents as any[]).filter((e) =>
                            eventStatusTab === "active"
                              ? Boolean(e?.active)
                              : !e?.active,
                          )
                        : []
                      ).map((event: EventType) => (
                        <TableRow
                          key={event.id}
                          sx={{
                            "&:hover": {
                              backgroundColor: "#f8fafc",
                            },
                          }}
                        >
                          <TableCell>
                            <Typography
                              sx={{
                                fontWeight: 500,
                                color: "#1e293b",
                                whiteSpace: "normal",
                              }}
                            >
                              {event.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CalendarToday
                                sx={{ fontSize: 16, mr: 1, color: "#64748b" }}
                              />
                              <Typography sx={{ color: "#475569" }}>
                                {parseDateLocale(event.starts_at)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell
                            sx={{ display: { xs: "none", md: "table-cell" } }}
                          >
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CalendarToday
                                sx={{ fontSize: 16, mr: 1, color: "#64748b" }}
                              />
                              <Typography sx={{ color: "#475569" }}>
                                {parseDateLocale(event.ends_at)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={event.active ? "Ativo" : "Finalizado"}
                              size="small"
                              sx={{
                                backgroundColor: event.active
                                  ? "#dcfce7"
                                  : "#fee2e2",
                                color: event.active ? "#166534" : "#991b1b",
                                fontWeight: 500,
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                rowGap: 1,
                              }}
                            >
                              <ViewButton
                                id={event.id}
                                updateEvents={syncEvents}
                                event={{
                                  active: event.active,
                                  cover: event.cover,
                                  created_at: new Date(event.created_at),
                                  description: event.description,
                                  ends_at: new Date(event.ends_at),
                                  name: event.name,
                                  updated_at: new Date(event.updated_at),
                                  starts_at: new Date(event.starts_at),
                                  id: event.id,
                                  limitdate: new Date(event.limitdate),
                                }}
                              />
                              <UpdateButton
                                id={event.id}
                                updateEvents={syncEvents}
                                event={{
                                  active: event.active,
                                  cover: event.cover,
                                  created_at: new Date(event.created_at),
                                  description: event.description,
                                  ends_at: new Date(event.ends_at),
                                  name: event.name,
                                  updated_at: new Date(event.updated_at),
                                  starts_at: new Date(event.starts_at),
                                  id: event.id,
                                  limitdate: new Date(event.limitdate),
                                  event_data: (event as any).event_data,
                                  event_address:
                                    (event as any).event_address || "",
                                }}
                              />
                              <DeleteButton
                                id={event.id}
                                updateEvents={syncEvents}
                              />
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Create Event Modal */}
      <ModalComponent
        open={open}
        handleClose={handleClose}
        title="Novo Evento"
        maxWidth={600}
      >
        <form onSubmit={handleSubmit}>
          <Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Basic Information */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                >
                  Informações Básicas
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <TextField
                    name="name"
                    label="Nome do Evento"
                    variant="outlined"
                    fullWidth
                    required
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    defaultValue={(latestEvent as any)?.name || ""}
                  />
                  <TextField
                    name="description"
                    label="Descrição"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={3}
                    required
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    defaultValue={(latestEvent as any)?.description || ""}
                  />
                  <TextField
                    name="cover"
                    label="URL da Capa"
                    variant="outlined"
                    fullWidth
                    size="small"
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    defaultValue={(latestEvent as any)?.cover || ""}
                  />
                </Box>
              </Box>

              {/* Event Dates */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                >
                  Datas do Evento
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <TextField
                    name="starts_at"
                    label="Data de Início"
                    type="date"
                    variant="outlined"
                    fullWidth
                    required
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    defaultValue={
                      (latestEvent as any)?.starts_at
                        ? toDateInput((latestEvent as any).starts_at)
                        : ""
                    }
                  />
                  <TextField
                    name="ends_at"
                    label="Data de Fim"
                    type="date"
                    variant="outlined"
                    fullWidth
                    required
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    defaultValue={
                      (latestEvent as any)?.ends_at
                        ? toDateInput((latestEvent as any).ends_at)
                        : ""
                    }
                  />
                  <TextField
                    name="limitdate"
                    label="Data Limite de Inscrição"
                    type="datetime-local"
                    variant="outlined"
                    fullWidth
                    required
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
                    defaultValue={
                      (latestEvent as any)?.limitdate
                        ? toLimitDateTimeInput((latestEvent as any).limitdate)
                        : ""
                    }
                  />
                </Box>
              </Box>

              {/* Pricing Configuration - Marcas Autorais */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                >
                  Configuração - Marcas Autorais
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <TextField
                    name="autoral_sub1"
                    label="Inscrição (1 Dia)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.autoral?.subscription_price_1 || 0
                    }
                  />
                  <TextField
                    name="autoral_sub2"
                    label="Inscrição (2 Dias)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.autoral?.subscription_price_2 || 0
                    }
                  />
                  <TextField
                    name="autoral_chair1"
                    label="Cadeira (1 Dia)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.autoral?.chair_price_1 || 0
                    }
                  />
                  <TextField
                    name="autoral_chair2"
                    label="Cadeira (2 Dias)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.autoral?.chair_price_2 || 0
                    }
                  />
                  <TextField
                    name="autoral_table1"
                    label="Mesa (1 Dia)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.autoral?.table_price_1 || 0
                    }
                  />
                  <TextField
                    name="autoral_table2"
                    label="Mesa (2 Dias)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.autoral?.table_price_2 || 0
                    }
                  />
                  <TextField
                    name="autoral_percentage"
                    label="Porcentagem (%)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={(latestData as any)?.autoral?.percentage || 0}
                  />
                </Box>
              </Box>

              {/* Pricing Configuration - Praça de Alimentação */}
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ mb: 2, fontWeight: 600, color: "#374151" }}
                >
                  Configuração - Praça de Alimentação
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  {/* <TextField
                    name="alimentacao_sub1"
                    label="Inscrição (1 Dia)"
                    type="number"
                    size="small"
                    fullWidth
                  /> */}
                  <TextField
                    name="alimentacao_sub2"
                    label="Inscrição (2 Dias)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.alimentacao?.subscription_price_2 ||
                      0
                    }
                  />
                  {/* <TextField
                    name="alimentacao_chair1"
                    label="Cadeira (1 Dia)"
                    type="number"
                    size="small"
                    fullWidth
                  /> */}
                  <TextField
                    name="alimentacao_chair2"
                    label="Cadeira (2 Dias)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.alimentacao?.chair_price_2 || 0
                    }
                  />
                  {/* <TextField
                    name="alimentacao_table1"
                    label="Mesa (1 Dia)"
                    type="number"
                    size="small"
                    fullWidth
                  /> */}
                  <TextField
                    name="alimentacao_table2"
                    label="Mesa (2 Dias)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.alimentacao?.table_price_2 || 0
                    }
                  />
                  <TextField
                    name="alimentacao_percentage"
                    label="Porcentagem (%)"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.alimentacao?.percentage || 0
                    }
                  />
                  <TextField
                    name="alimentacao_bike"
                    label="Bike"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.alimentacao?.bike || 0
                    }
                  />
                  <TextField
                    name="alimentacao_trailer"
                    label="Trailer"
                    type="number"
                    size="small"
                    fullWidth
                    defaultValue={
                      (latestData as any)?.alimentacao?.trailer || 0
                    }
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              p: 3,
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={adminButtonOutlinedSx}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="contained" sx={adminButtonPrimarySx}>
              Criar Evento
            </Button>
          </Box>
        </form>
      </ModalComponent>
    </Box>
  );
}
